import { useTranslation } from "react-i18next";
import React, { useState, useCallback, useMemo, useEffect } from "react";

// How long cards without cached pricing wait on the live lookup before
// falling back to "Call for Price"
const LIVE_PRICE_WAIT_MS = 5000;
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ProductCatalogCard } from "@/components/products/ProductCatalogCard";
import { ProductDetailsDialog } from "@/components/products/ProductDetailsDialog";
import { AiRerankLoading } from "@/components/products/AiRerankLoading";
import { useProductEvents } from "@/hooks/use-product-events";
import { useHajocaInventory, applyLiveInventory } from "@/hooks/use-hajoca-inventory";
import { Product } from "@/types/product";

/** A product selected in the catalog, keyed by id with quantity and optional price. */
export interface SelectedProduct {
  productId: string;
  quantity: number;
  unitPrice?: number;
}

interface ProductGridProps {
  products: Product[];
  selectedProducts: SelectedProduct[];
  onProductSelect: (product: Product, quantity: number) => void;
  showCachedPricing?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  /** Show the "AI is thinking" animation instead of skeletons while loading. */
  aiThinking?: boolean;
  /** Subtle tell that the active re-rank ran on the OpenAI backup. */
  isOpenAiRerank?: boolean;
  /** Elasticsearch retrieval — swaps in the warm loading animation. */
  isElasticSearch?: boolean;
  /** Active rerank model, shown alongside the ES badge (e.g. "ES + Claude"). */
  rerankModel?: string;
  hasMore?: boolean;
  totalCount?: number;
  onLoadMore?: () => void;
  emptyStateMessage?: string;
  emptyStateAction?: React.ReactNode;
  getProductNotes?: (product: Product) => string | null;
  isPurchased?: (productId: string) => boolean;
  searchQuery?: string;
  searchId?: string;
}

export const ProductGrid: React.FC<ProductGridProps> = ({
  products,
  selectedProducts,
  onProductSelect,
  showCachedPricing = false,
  disabled = false,
  isLoading = false,
  aiThinking = false,
  isOpenAiRerank = false,
  isElasticSearch = false,
  rerankModel,
  hasMore = false,
  totalCount = 0,
  onLoadMore,
  emptyStateMessage,
  emptyStateAction,
  getProductNotes,
  isPurchased,
  searchQuery = "",
  searchId,
}) => {
  const { t } = useTranslation();
  const { capture } = useProductEvents({ component: "ProductGrid" });
  const [selectedProductForDialog, setSelectedProductForDialog] =
    useState<Product | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Live pricing overlay: one batched lookup for the visible page. Cards
  // render immediately from cached pricing (org_product_pricing via the
  // products API); when live values land they win. On error/timeout/missing
  // rows the cached values simply remain — no fallback branching needed.
  const { data: liveInventory, isLoading: isLiveInventoryLoading } =
    useHajocaInventory(products, {
      enabled: showCachedPricing,
    });

  // Products without a cached price shouldn't declare "Call for Price" while
  // the live lookup might still supply one. Cap the wait so a hung request
  // can't hide the fallback indefinitely (observed live latency is 1-2s).
  const [liveWaitExpired, setLiveWaitExpired] = useState(false);
  useEffect(() => {
    if (!isLiveInventoryLoading) return;
    setLiveWaitExpired(false);
    const timer = setTimeout(() => setLiveWaitExpired(true), LIVE_PRICE_WAIT_MS);
    return () => clearTimeout(timer);
  }, [isLiveInventoryLoading]);
  const awaitingLivePrices = isLiveInventoryLoading && !liveWaitExpired;

  const displayProducts = useMemo(() => {
    if (!liveInventory) return products;
    return applyLiveInventory(products, liveInventory.byProduct);
  }, [products, liveInventory]);

  const getSelectedQuantity = (productId: string): number => {
    const selected = selectedProducts.find((p) => p.productId === productId);
    return selected?.quantity || 0;
  };

  const handleCardClick = useCallback((product: Product) => {
    setSelectedProductForDialog(product);
    setDialogOpen(true);
  }, []);

  const handleDialogClose = useCallback((open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setSelectedProductForDialog(null);
    }
  }, []);

  const handleLoadMore = useCallback(() => {
    if (onLoadMore) {
      // Track load more event with pure properties
      capture({
        event: "load_more_products",
        context: {
          searchQuery: searchQuery || undefined,
          searchId: searchId,
          currentProductCount: products.length,
          totalCount: totalCount,
          selectedProductCount: selectedProducts.length,
        },
      });
      onLoadMore();
    }
  }, [onLoadMore, capture, searchQuery, searchId, products.length, totalCount, selectedProducts.length]);

  return (
    <Card>
      <CardContent className="p-6 max-h-[600px] md:max-h-[700px] overflow-y-auto">
        {isLoading && aiThinking ? (
          <AiRerankLoading
            isOpenAi={isOpenAiRerank}
            isElastic={isElasticSearch}
            rerankModel={rerankModel}
          />
        ) : isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <Skeleton className="w-full h-32 mb-4" />
                  <Skeleton className="h-4 w-3/4 mb-2" />
                  <Skeleton className="h-3 w-1/2 mb-4" />
                  <div className="flex justify-between items-center">
                    <Skeleton className="h-6 w-20" />
                    <Skeleton className="h-8 w-8 rounded" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {displayProducts.map((product) => {
                const quantity = getSelectedQuantity(product.uid);
                const isProductPurchased = isPurchased?.(product.uid) || false;

                return (
                  <ProductCatalogCard
                    key={product.uid}
                    product={product}
                    quantity={quantity}
                    onQuantityChange={(quantity) =>
                      onProductSelect(product, quantity)
                    }
                    showCachedPricing={showCachedPricing}
                    disabled={disabled}
                    isPurchased={isProductPurchased}
                    getProductNotes={getProductNotes}
                    onCardClick={() => handleCardClick(product)}
                    isPriceLoading={awaitingLivePrices}
                  />
                );
              })}
            </div>

            {/* Load More Button */}
            {hasMore && onLoadMore && products.length > 0 && (
              <div className="flex justify-center items-center gap-4 pt-6 flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleLoadMore}
                  disabled={disabled}
                >
                  {t("components.products.productGrid.loadMoreProducts")}{products.length} of {totalCount})
                </Button>
              </div>
            )}

            {/* Empty State */}
            {products.length === 0 && !isLoading && (
              <div className="text-center py-12 text-muted-foreground">
                <div className="text-lg font-medium mb-2">
                  {t("components.products.productGrid.noProductsFound")}
                </div>
                {emptyStateMessage && (
                  <p className="text-sm mb-4">{emptyStateMessage}</p>
                )}
                {emptyStateAction}
              </div>
            )}
          </>
        )}
      </CardContent>
      {selectedProductForDialog && (
        <ProductDetailsDialog
          product={selectedProductForDialog}
          open={dialogOpen}
          onOpenChange={handleDialogClose}
          quantity={getSelectedQuantity(selectedProductForDialog.uid)}
          onQuantityChange={(quantity) =>
            onProductSelect(selectedProductForDialog, quantity)
          }
          showCachedPricing={showCachedPricing}
          disabled={disabled}
        />
      )}
    </Card>
  );
};
